import { Grid } from "@material-ui/core";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import { useGetActivityStatusReasonsQuery } from "features/activity/activitySlice";
import ServiceListDialog from "features/vms/vendorDashboard/ServicesGraphWidget/ServiceListDialog";
import { useMemo } from "react";
import { Bar, BarChart, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { arrayToObject } from "utils/object-util";
import WidgetTitle from "../FleetManagementDashboard/WidgetTitle/WidgetTitle";
import { NewWashDashboardStyle } from "./NewWashDashboardStyle";

export const ActivityExceptionsWidget = ({ activities, lots, isLoading }) => {
    const classes = NewWashDashboardStyle();

    let { data: statusReasons, error: statusReasonsError, isLoading: isLoadingStatusReasons } = useGetActivityStatusReasonsQuery();
    const reasonsLookup = arrayToObject(statusReasons);
    const lotsLookup = arrayToObject(lots);

    const chartData = useMemo(() => {
        if (!activities || !lots || !statusReasons) return [];
        // Group by lot and reason
        const grouped = {};
        activities.forEach(act => {
            const lotId = act.lotId;
            const reasonId = act.reasonId;
            if (!grouped[lotId]) grouped[lotId] = {};
            if (!grouped[lotId][reasonId]) grouped[lotId][reasonId] = 0;
            grouped[lotId][reasonId]++;
        });
        // Build chart data array
        return Object.entries(grouped).map(([lotId, reasons]) => {
            const out = { lot: lotsLookup[parseInt(lotId)]?.name || 'Unknown Lot' };
            Object.entries(reasons).forEach(([reasonId, count]) => {
                if (count > 0 && reasonsLookup[reasonId]) {
                    out[reasonsLookup[reasonId].name] = count;
                }
            });
            return out;
        });
    }, [activities, lots, reasonsLookup]);

    const reasonNames = statusReasons?.map(r => r.reasonText) ?? [];

    const blueShades = ['#0d47a1', '#0288d1', '#00bcd4', '#81d4fa'];

    const tickStyle = {
        color: 'darkGray',
        fontSize: '0.7em',
        fontWeight: '600',
    }

    const mapExportData = (a => {
        const reasons = [
            'Inclement weather',
            'Off the lot most of the cycle',
            'Was being worked on',
            'Other'
        ];
        const exceptionsSum = reasons.reduce((sum, reason) => sum + (a?.[reason] || 0), 0);
        return {
            ['Lot']: a?.['lot'],
            ['Inclement weather']: a?.['Inclement weather'],
            ['Off the lot most of the cycle']: a?.['Off the lot most of the cycle'],
            ['Was being worked on']: a?.['Was being worked on'],
            ['Other']: a?.['Other'],
            ['Total Exceptions']: exceptionsSum,
        };
    });

    return (
        <Grid container direction='column'>
            <Grid container direction='row'>
                <Grid item xs={11}>
                    <WidgetTitle
                        title="Exceptions"
                        mapExportData={mapExportData}
                        dataToExport={chartData}
                        loading={isLoading || isLoadingStatusReasons}
                    />
                </Grid>
                <Grid item xs={1} className={classes.exceptionTableIconWrapper}>
                    <ServiceListDialog activities={activities} title={'Exceptions'} />
                </Grid>
            </Grid>
            <Grid item>
                {!!chartData?.length ? <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                        <XAxis
                            dataKey="lot"
                            tickLine={false}
                            tick={{...tickStyle, angle: -20, textAnchor: 'end' }}
                            interval={0}
                        />
                        <YAxis
                            allowDecimals={false}
                            tickLine={false}
                            tick={tickStyle}
                        />
                        <Tooltip />
                        <Legend
                            iconType='circle'
                            layout="horizontal"
                            verticalAlign="bottom"
                            iconSize={10}
                            formatter={(value, entry, index) => <span style={{ fontSize: '0.8em' }}>{value}</span>} />
                        {reasonNames.map((name, idx) => (
                            <Bar key={name}
                                dataKey={name}
                                stackId={null}
                                fill={blueShades[idx % blueShades.length]}
                            >
                                <LabelList dataKey={name} position="inside" style={{ fill: 'white', fontSize: '0.8em' }} />
                            </Bar>
                        ))}
                    </BarChart>
                </ResponsiveContainer> :
                    <EmptyPlaceholder text='No Exceptions' />}
            </Grid>
        </Grid>
    )
}